import { useEffect, useMemo, useState } from 'react';
import {
  generateQueryString,
  IHttpResponse,
} from '@qlibs/react-components';
import useReportLog from '../../../../hooks/useReportLog';
import { httpRequest } from '../../../../helpers/api';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import {
  EReportDataInterval,
  ILogGPS,
  ILogReport,
} from '../../types/report-log.type';

const monthTrackingInterval = EReportDataInterval.day;

// disabled: the PDF page already has the tracking from the backend
export const useVesselTrackingGps = (options?: { disabled?: boolean }) => {
  const disabled = Boolean(options?.disabled);
  const { asset, filter, timezone } = useAppliedReportFilter();
  const { logs } = useReportLog();
  const [gpsLogs, setGpsLogs] = useState<ILogGPS | undefined>();
  const [loading, setLoading] = useState(false);

  const isMonthTrackingOverride =
    filter.interval === EReportDataInterval.month;

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    const fetchGpsLogs = async () => {
      try {
        if (!asset.assetId) {
          setGpsLogs({});
          return;
        }

        setLoading(true);

        const currentTimezone = filter.timezone || timezone || 7;

        const requestParams = {
          assetId: asset.assetId,
          dataSource: filter.dataSource || '1',
          deviceType: 'gps',
          endAt: filter.endAt,
          interval: monthTrackingInterval,
          startAt: filter.startAt,
          timezone: currentTimezone,
        };

        const result = await httpRequest.get<IHttpResponse<ILogReport>>(
          `report-log${generateQueryString(requestParams)}`,
          {
            signal: controller.signal,
          }
        );

        if (!ignore) {
          setGpsLogs(result.data.payload?.gps || {});
        }
      } catch (error: any) {
        if (
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED' ||
          controller.signal.aborted
        ) {
          return;
        }

        console.error('failed get month vessel tracking gps', error);
        if (!ignore) {
          setGpsLogs({});
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    if (!isMonthTrackingOverride || disabled) {
      setGpsLogs(undefined);
      setLoading(false);
      controller.abort();
      return () => {
        ignore = true;
      };
    }

    fetchGpsLogs();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [
    asset.assetId,
    filter.endAt,
    filter.dataSource,
    filter.interval,
    filter.startAt,
    filter.timezone,
    isMonthTrackingOverride,
    timezone,
    disabled,
  ]);

  const resolvedGpsLogs = useMemo(() => {
    if (isMonthTrackingOverride) {
      return gpsLogs || {};
    }

    return logs.gps || {};
  }, [gpsLogs, isMonthTrackingOverride, logs.gps]);

  return {
    gpsLogs: resolvedGpsLogs,
    isMonthTrackingOverride,
    loading,
    trackingInterval: monthTrackingInterval,
  };
};
